import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  ArrowLeft,
  Download,
  Filter,
  Users,
  Calendar,
  FileText,
  BarChart3,
  PieChart,
  TrendingUp,
  Search,
  User,
  Group,
  BookOpen,
  Clock,
  Award,
  Target,
  Building2
} from "lucide-react";
import { toast } from "sonner";
import { ComprehensiveAnalytics } from './ComprehensiveAnalytics';
import { DepartmentManager } from './DepartmentManager';
import { supabase } from '@/integrations/supabase/client';

interface ReportUser {
  id: string;
  name: string;
  email: string;
  department: string;
  role: string;
  hire_date: string;
  courses_completed: number;
  courses_in_progress: number;
  total_hours: number;
  certifications: string[];
  last_activity: string;
  compliance_status: 'compliant' | 'warning' | 'overdue';
}

interface ReportData {
  users: ReportUser[];
  summary: {
    total_users: number;
    avg_completion_rate: number;
    total_hours: number;
    most_popular_course: string;
    compliance_rate: number;
  };
}

export default function AdminReporting() {
  const navigate = useNavigate();
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [reportType, setReportType] = useState<'individual' | 'group' | 'comprehensive'>('comprehensive');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
  const [selectedSchools, setSelectedSchools] = useState<string[]>([]);
  const [reportData, setReportData] = useState<ReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Mock data for demonstration
  const mockUsers: ReportUser[] = [
    {
      id: '1',
      name: 'Sarah Johnson',
      email: 'sarah.johnson@district.edu',
      department: 'Elementary Education',
      role: 'Teacher',
      hire_date: '2020-08-15',
      courses_completed: 8,
      courses_in_progress: 2,
      total_hours: 45,
      certifications: ['CPR', 'Special Needs'],
      last_activity: '2024-03-15',
      compliance_status: 'compliant'
    },
    {
      id: '2',
      name: 'Michael Chen',
      email: 'michael.chen@district.edu',
      department: 'High School Math',
      role: 'Department Head',
      hire_date: '2018-01-10',
      courses_completed: 12,
      courses_in_progress: 1,
      total_hours: 78,
      certifications: ['Advanced Math', 'Leadership'],
      last_activity: '2024-03-20',
      compliance_status: 'compliant'
    },
    {
      id: '3',
      name: 'Emily Rodriguez',
      email: 'emily.rodriguez@district.edu',
      department: 'Administration',
      role: 'Principal',
      hire_date: '2015-07-01',
      courses_completed: 15,
      courses_in_progress: 3,
      total_hours: 95,
      certifications: ['Administrative Leadership', 'Curriculum Development'],
      last_activity: '2024-03-18',
      compliance_status: 'warning'
    }
  ];

  const departments = ['Elementary Education', 'High School Math', 'Administration', 'Special Education', 'Physical Education'];
  const schools = ['Roosevelt Elementary', 'Lincoln Middle School', 'Washington High School', 'Jefferson Elementary', 'Madison High School', 'Adams Middle School'];

  // Fetch users for search
  useEffect(() => {
    const fetchUsers = async () => {
      if (searchTerm.length < 2) {
        setAvailableUsers([]);
        return;
      }
      
      setLoadingUsers(true);
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, first_name, last_name, email, department')
          .or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,department.ilike.%${searchTerm}%`)
          .limit(20);

        if (error) throw error;

        const formattedUsers = (data || []).map(u => ({
          id: u.id,
          name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unknown',
          email: u.email || '',
          department: u.department || 'N/A'
        }));

        setAvailableUsers(formattedUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
      } finally {
        setLoadingUsers(false);
      }
    };

    const debounce = setTimeout(fetchUsers, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  const reportSections = [
    {
      id: 'overview',
      label: 'Executive Overview',
      description: 'High-level summary and key metrics',
      icon: BarChart3,
      included: true
    },
    {
      id: 'individual_progress',
      label: 'Individual Progress',
      description: 'Detailed user-by-user progress tracking',
      icon: User,
      included: true
    },
    {
      id: 'course_analytics',
      label: 'Course Analytics',
      description: 'Popular courses, completion rates, engagement',
      icon: BookOpen,
      included: true
    },
    {
      id: 'compliance_tracking',
      label: 'Compliance Tracking',
      description: 'Required training completion status',
      icon: Target,
      included: true
    },
    {
      id: 'time_analysis',
      label: 'Time Analysis',
      description: 'Learning hours, session durations, peak usage',
      icon: Clock,
      included: false
    },
    {
      id: 'certifications',
      label: 'Certifications & Badges',
      description: 'Earned credentials and achievement tracking',
      icon: Award,
      included: false
    },
    {
      id: 'department_comparison',
      label: 'Department Comparison',
      description: 'Cross-department performance analysis',
      icon: Group,
      included: false
    },
    {
      id: 'quarterly_trends',
      label: 'Quarterly Trends',
      description: 'Learning activity trends over time',
      icon: TrendingUp,
      included: false
    }
  ];

  const [includedSections, setIncludedSections] = useState(
    reportSections.reduce((acc, section) => ({
      ...acc,
      [section.id]: section.included
    }), {})
  );

  const generateReport = async () => {
    if (!dateRange.start || !dateRange.end) {
      toast.error('Please select a date range');
      return;
    }

    setLoading(true);
    
    try {
      // Build query for profiles
      let query = supabase
        .from('profiles')
        .select(`
          id,
          first_name,
          last_name,
          email,
          department,
          school_name,
          job_type,
          hire_date
        `);

      // Apply filters
      if (selectedUsers.length > 0) {
        query = query.in('id', selectedUsers);
      } else if (searchTerm) {
        query = query.or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,department.ilike.%${searchTerm}%,school_name.ilike.%${searchTerm}%`);
      }

      if (selectedDepartments.length > 0) {
        query = query.in('department', selectedDepartments);
      }

      if (selectedSchools.length > 0) {
        query = query.in('school_name', selectedSchools);
      }

      const { data: profilesData, error } = await query;

      if (error) {
        console.error('Error fetching profiles:', error);
        toast.error('Failed to generate report');
        setLoading(false);
        return;
      }

      if (!profilesData || profilesData.length === 0) {
        toast.warning('No users match the selected filters. Please adjust your search criteria.');
        setLoading(false);
        return;
      }

      // Fetch enrollments separately for each user
      const usersPromises = (profilesData || []).map(async (profile) => {
        const { data: enrollments } = await supabase
          .from('enrollments')
          .select(`
            id,
            status,
            course_id,
            courses(title, duration_hours)
          `)
          .eq('participant_id', profile.id);

        const completed = enrollments?.filter(e => e.status === 'completed').length || 0;
        const inProgress = enrollments?.filter(e => e.status === 'enrolled').length || 0;
        const totalHours = enrollments
          ?.filter(e => e.status === 'completed' && e.courses)
          .reduce((sum, e) => sum + (e.courses?.duration_hours || 0), 0) || 0;

        return {
          id: profile.id,
          name: `${profile.first_name || ''} ${profile.last_name || ''}`.trim(),
          email: profile.email || '',
          department: profile.department || 'N/A',
          role: profile.job_type || 'N/A',
          hire_date: profile.hire_date || '',
          courses_completed: completed,
          courses_in_progress: inProgress,
          total_hours: totalHours,
          certifications: [],
          last_activity: profile.hire_date || '',
          compliance_status: totalHours >= 20 ? 'compliant' : totalHours >= 10 ? 'warning' : 'overdue'
        } as ReportUser;
      });

      const users = await Promise.all(usersPromises);

      // Calculate summary
      const totalHours = users.reduce((sum, user) => sum + user.total_hours, 0);
      const avgCompletionRate = users.length > 0 
        ? (users.reduce((sum, u) => sum + u.courses_completed, 0) / users.length) * 10 
        : 0;
      const compliantUsers = users.filter(u => u.compliance_status === 'compliant').length;
      const complianceRate = users.length > 0 ? (compliantUsers / users.length) * 100 : 0;

      setReportData({
        users,
        summary: {
          total_users: users.length,
          avg_completion_rate: Math.round(avgCompletionRate * 10) / 10,
          total_hours: totalHours,
          most_popular_course: 'Classroom Management Essentials',
          compliance_rate: Math.round(complianceRate * 10) / 10
        }
      });
      
      setShowPreview(true);
      toast.success(`Report generated for ${users.length} users`);
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = () => {
    // In a real implementation, this would generate and download a Word document
    toast.success("Report generated! Download will begin shortly.");
    
    // Simulate download
    const element = document.createElement('a');
    element.href = '#';
    element.download = `edugogy-report-${new Date().toISOString().split('T')[0]}.docx`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const handleSectionToggle = (sectionId: string, checked: boolean) => {
    setIncludedSections(prev => ({
      ...prev,
      [sectionId]: checked
    }));
  };

  const getComplianceColor = (status: string) => {
    switch (status) {
      case 'compliant': return 'text-green-600';
      case 'warning': return 'text-yellow-600';
      case 'overdue': return 'text-red-600';
      default: return 'text-muted-foreground';
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Comprehensive Reporting</h1>
          <p className="text-muted-foreground">Generate detailed reports on user progress and learning analytics</p>
        </div>
      </div>

      <Tabs defaultValue="analytics" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="analytics">Analytics Dashboard</TabsTrigger>
          <TabsTrigger value="builder">Report Builder</TabsTrigger>
          <TabsTrigger value="departments">Departments</TabsTrigger>
          <TabsTrigger value="preview" disabled={!reportData}>Report Preview</TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="space-y-6">
          <ComprehensiveAnalytics />
        </TabsContent>

        <TabsContent value="departments" className="space-y-6">
          <DepartmentManager />
        </TabsContent>

        <TabsContent value="builder" className="space-y-6">
          {/* Report Configuration */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Filters */}
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Report Filters
                </CardTitle>
                <CardDescription>Configure your report parameters</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Report Type */}
                <div>
                  <Label>Report Type</Label>
                  <Select value={reportType} onValueChange={(value: any) => setReportType(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="individual">Individual User</SelectItem>
                      <SelectItem value="group">Group/Department</SelectItem>
                      <SelectItem value="comprehensive">Comprehensive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Search Users */}
                <div>
                  <Label>Search Users</Label>
                  <Popover open={userSearchOpen} onOpenChange={setUserSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={userSearchOpen}
                        className="w-full justify-between"
                      >
                        {selectedUsers.length > 0
                          ? `${selectedUsers.length} user(s) selected`
                          : "Search by name or email..."}
                        <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command>
                        <CommandInput 
                          placeholder="Type to search users..." 
                          value={searchTerm}
                          onValueChange={setSearchTerm}
                        />
                        <CommandList>
                          {loadingUsers && (
                            <div className="py-6 text-center text-sm">Loading users...</div>
                          )}
                          {!loadingUsers && searchTerm.length < 2 && (
                            <div className="py-6 text-center text-sm">Type at least 2 characters to search</div>
                          )}
                          {!loadingUsers && searchTerm.length >= 2 && availableUsers.length === 0 && (
                            <CommandEmpty>No users found.</CommandEmpty>
                          )}
                          {!loadingUsers && availableUsers.length > 0 && (
                            <CommandGroup>
                              {availableUsers.map((user) => (
                                <CommandItem
                                  key={user.id}
                                  value={user.email}
                                  onSelect={() => {
                                    setSelectedUsers(prev => 
                                      prev.includes(user.id) 
                                        ? prev.filter(id => id !== user.id)
                                        : [...prev, user.id]
                                    );
                                  }}
                                >
                                  <Checkbox
                                    checked={selectedUsers.includes(user.id)}
                                    className="mr-2"
                                  />
                                  <div className="flex flex-col">
                                    <span className="font-medium">{user.name}</span>
                                    <span className="text-xs text-muted-foreground">{user.email}</span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          )}
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  {selectedUsers.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedUsers([])}
                      className="mt-1 h-6 text-xs"
                    >
                      Clear selection
                    </Button>
                  )}
                </div>

                {/* Department Filter */}
                <div>
                  <Label>Departments</Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {departments.map((dept) => (
                      <div key={dept} className="flex items-center space-x-2">
                        <Checkbox
                          id={dept}
                          checked={selectedDepartments.includes(dept)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedDepartments([...selectedDepartments, dept]);
                            } else {
                              setSelectedDepartments(selectedDepartments.filter(d => d !== dept));
                            }
                          }}
                        />
                        <Label htmlFor={dept} className="text-sm">{dept}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Schools Filter */}
                <div>
                  <Label>Schools</Label>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {schools.map((school) => (
                      <div key={school} className="flex items-center space-x-2">
                        <Checkbox
                          id={school}
                          checked={selectedSchools.includes(school)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setSelectedSchools([...selectedSchools, school]);
                            } else {
                              setSelectedSchools(selectedSchools.filter(s => s !== school));
                            }
                          }}
                        />
                        <Label htmlFor={school} className="text-sm">{school}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Date Range */}
                <div>
                  <Label>Date Range</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      type="date"
                      value={dateRange.start}
                      onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    />
                    <Input
                      type="date"
                      value={dateRange.end}
                      onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Report Sections */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Report Sections
                </CardTitle>
                <CardDescription>Select which sections to include in your report</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {reportSections.map((section) => {
                    const IconComponent = section.icon;
                    return (
                      <div key={section.id} className="flex items-start space-x-3 p-3 border rounded-lg">
                        <Checkbox
                          id={section.id}
                          checked={includedSections[section.id] || false}
                          onCheckedChange={(checked) => handleSectionToggle(section.id, !!checked)}
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <IconComponent className="h-4 w-4 text-primary" />
                            <Label htmlFor={section.id} className="font-medium">
                              {section.label}
                            </Label>
                          </div>
                          <p className="text-sm text-muted-foreground">{section.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Generate Report */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Ready to Generate Report?</h3>
                  <p className="text-muted-foreground">
                    Selected {Object.values(includedSections).filter(Boolean).length} sections
                    {selectedDepartments.length > 0 && ` • ${selectedDepartments.length} departments`}
                    {searchTerm && ` • Filtered by "${searchTerm}"`}
                  </p>
                </div>
                <Button onClick={generateReport} disabled={loading} size="lg">
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                      Generating...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Generate Report
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="preview" className="space-y-6">
          {reportData && (
            <>
              {/* Report Header */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-2xl">Learning Analytics Report</CardTitle>
                      <CardDescription>
                        Generated on {new Date().toLocaleDateString()} • 
                        {reportData.users.length} users • 
                        {Object.values(includedSections).filter(Boolean).length} sections
                      </CardDescription>
                    </div>
                    <Button onClick={downloadReport} className="flex items-center gap-2">
                      <Download className="h-4 w-4" />
                      Download Word Document
                    </Button>
                  </div>
                </CardHeader>
              </Card>

              {/* Executive Summary */}
              {(includedSections as any).overview && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="h-5 w-5" />
                      Executive Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 border rounded-lg">
                        <div className="text-2xl font-bold text-primary">{reportData.summary.total_users}</div>
                        <div className="text-sm text-muted-foreground">Total Users</div>
                      </div>
                      <div className="text-center p-4 border rounded-lg">
                        <div className="text-2xl font-bold text-green-600">{reportData.summary.avg_completion_rate}%</div>
                        <div className="text-sm text-muted-foreground">Avg Completion</div>
                      </div>
                      <div className="text-center p-4 border rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">{reportData.summary.total_hours}</div>
                        <div className="text-sm text-muted-foreground">Total Hours</div>
                      </div>
                      <div className="text-center p-4 border rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">{reportData.summary.compliance_rate}%</div>
                        <div className="text-sm text-muted-foreground">Compliance Rate</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Individual Progress */}
              {(includedSections as any).individual_progress && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Individual Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {reportData.users.map((user) => (
                        <div key={user.id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h4 className="font-semibold">{user.name}</h4>
                              <p className="text-sm text-muted-foreground">{user.department} • {user.role}</p>
                            </div>
                            <Badge variant={user.compliance_status === 'compliant' ? 'default' : 'destructive'}>
                              {user.compliance_status}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Completed:</span>
                              <span className="ml-2 font-medium">{user.courses_completed} courses</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">In Progress:</span>
                              <span className="ml-2 font-medium">{user.courses_in_progress} courses</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Total Hours:</span>
                              <span className="ml-2 font-medium">{user.total_hours}h</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Last Active:</span>
                              <span className="ml-2 font-medium">{new Date(user.last_activity).toLocaleDateString()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Additional sections would be rendered here based on includedSections */}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}